import {
  Button,
  Checkbox,
  Dialog,
  SelectField,
  TextAreaField,
} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';
import {toast} from 'react-toastify';

import {backendHost} from '../config';
import {useViewer} from '../state/viewer/hooks';
import {setViewerUsingAdminPrivileges, viewerIsWbAdmin} from '../util/admin';
import {
  type AccessRequestDurationType,
  type AccessRequestRoleListType,
  type AccessRequestRoleType,
  EIGHT_HOURS,
  FOUR_HOURS,
  ONE_HOUR,
  REQUEST_DURATION_KEY,
  REQUEST_REASON_KEY,
  REQUEST_ROLES_KEY,
  TWO_HOURS,
  WANDB_ADMIN_ROLE,
} from '../util/teleportAccessRequest';
import {
  calculateTtl,
  useTeleportAccessRequests,
} from './Search/SearchNav/useTeleportAccessRequests';

interface TeleportAccessRequestModalProps {
  onClose: () => void;
}
const INITIAL_ACCESS_REQUEST_DURATION: AccessRequestDurationType = EIGHT_HOURS;

const ACCESS_REQUEST_DURATION_OPTIONS: {
  id: AccessRequestDurationType;
  label: string;
}[] = [
  {id: ONE_HOUR, label: '1h'},
  {id: TWO_HOURS, label: '2h'},
  {id: FOUR_HOURS, label: '4h'},
  {id: EIGHT_HOURS, label: '8h'},
  // {id: TWELVE_HOURS, label: '12h'}, - Temporarily disabled see https://weightsandbiases.slack.com/archives/CD72PQ03B/p1781552853252019
];

const ACCESS_REQUEST_ROLE_OPTIONS: {
  text: string;
  value: AccessRequestRoleType;
}[] = [{text: 'wandb-admin', value: WANDB_ADMIN_ROLE}];

const TeleportAccessRequestModal: React.FC<TeleportAccessRequestModalProps> = ({
  onClose,
}) => {
  const viewer = useViewer();
  const wasWbAdminWhenCheckingForTeleportAccessRequests =
    viewerIsWbAdmin(viewer);
  const {
    approvedTeleportAccessRequests,
    isFetchingApprovedTeleportAccessRequests,
  } = useTeleportAccessRequests(
    wasWbAdminWhenCheckingForTeleportAccessRequests
  );

  const hasApprovedTeleportAccessRequest =
    Object.keys(approvedTeleportAccessRequests).length > 0;

  const [accessRequestDuration, setAccessRequestDuration] =
    useState<AccessRequestDurationType>(INITIAL_ACCESS_REQUEST_DURATION);

  // By default, pre-check the wandb-admin role
  const initialAccessRequestRoles: AccessRequestRoleListType = [
    WANDB_ADMIN_ROLE,
  ];
  const [accessRequestRoles, setAccessRequestRoles] =
    useState<AccessRequestRoleListType>(initialAccessRequestRoles);

  const [accessRequestReason, setAccessRequestReason] = useState<string>('');

  const handleCheckboxChange = (role: AccessRequestRoleType) =>
    setAccessRequestRoles(prevRoles => {
      if (prevRoles.includes(role)) {
        return prevRoles.filter(r => r !== role);
      }
      return [...prevRoles, role];
    });

  const onSubmit = async (
    accessRequestRoles: AccessRequestRoleListType,
    accessRequestDuration: AccessRequestDurationType,
    accessRequestReason: string
  ) => {
    const response = await fetch(backendHost(`/teleport/access_requests`), {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        [REQUEST_ROLES_KEY]: accessRequestRoles,
        [REQUEST_DURATION_KEY]: accessRequestDuration,
        [REQUEST_REASON_KEY]: accessRequestReason,
      }),
      credentials: 'include',
    });

    if (response.ok) {
      // If request is approved, set admin privileges and reload the page
      setViewerUsingAdminPrivileges(true);
      window.location.reload();
    } else {
      toast.error('Teleport access request denied.');
      onClose();
    }
  };

  return (
    <Dialog.Modal
      isOpen
      onOpenChange={open => {
        if (!open) {
          onClose();
        }
      }}>
      <Dialog.Heading>Request Access for Teleport Roles</Dialog.Heading>
      <Dialog.Content>
        <TailwindContents>
          {!isFetchingApprovedTeleportAccessRequests && (
            <div className="flex flex-col pb-16">
              <h3>Approved Roles</h3>
              {hasApprovedTeleportAccessRequest && (
                <div className="mb-8 flex justify-between text-sm font-normal text-wb-text-secondary">
                  <span className="basis-2/5 text-left">Role</span>
                  <span className="basis-3/5 text-left">Expires in</span>
                </div>
              )}
              {hasApprovedTeleportAccessRequest &&
                Object.entries(approvedTeleportAccessRequests).map(
                  ([role, timestamp]) => (
                    <div key={role} className="mb-8 flex justify-end">
                      <span className="basis-2/5 text-left font-normal">
                        {role}
                      </span>
                      <span className="basis-3/5 text-left">
                        {calculateTtl(timestamp)}
                      </span>
                    </div>
                  )
                )}

              {!hasApprovedTeleportAccessRequest && (
                <div className="flex">
                  <span className="flex-auto">No approved roles found</span>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col pb-16">
            <h3>Request Roles</h3>

            <div className="mb-8 flex justify-between text-sm font-normal">
              <span className="basis-[30%] text-left">Selected</span>
              <span className="basis-[70%] text-left">Role</span>
            </div>

            {ACCESS_REQUEST_ROLE_OPTIONS.map(option => (
              <div key={option.value} className="mb-8 flex justify-between">
                <span className="flex basis-[30%] items-center text-left">
                  <Checkbox
                    isSelected={accessRequestRoles.includes(option.value)}
                    onChange={() => handleCheckboxChange(option.value)}
                    aria-label={option.text}
                  />
                </span>
                <span className="basis-[70%] text-left">{option.text}</span>
              </div>
            ))}
          </div>
          <div className="flex items-baseline justify-between pb-16">
            <h3>Request Duration</h3>
            <SelectField
              aria-label="Request duration"
              items={ACCESS_REQUEST_DURATION_OPTIONS}
              value={accessRequestDuration}
              onChange={key => {
                if (key != null) {
                  setAccessRequestDuration(key as AccessRequestDurationType);
                }
              }}
            />
          </div>
          <div className="flex flex-col">
            <h3>Request Reason</h3>
            <TextAreaField
              aria-label="Request reason"
              placeholder="Enter your request reason (max 2000 characters)"
              value={accessRequestReason}
              onChange={setAccessRequestReason}
              maxLength={2000}
              fullWidth
              rows={4}
              resize="vertical"
            />
          </div>
          <div className="mt-16 mb-32 text-sm text-wb-text-tertiary">
            If your access request is not auto-approved, please try logging into
            Teleport via Okta. Then, try again. Due to a change in Teleport user
            provisioning, users that logged into Teleport before 2/13/2025 may
            need to log into Teleport again.
          </div>
        </TailwindContents>
      </Dialog.Content>
      <Dialog.Footer>
        <Button
          onPress={() =>
            onSubmit(
              accessRequestRoles,
              accessRequestDuration,
              accessRequestReason
            )
          }
          isDisabled={
            accessRequestRoles.length === 0 || accessRequestReason.trim() === ''
          }>
          Submit
        </Button>
        <Button variant="secondary" onPress={onClose}>
          Cancel
        </Button>
      </Dialog.Footer>
    </Dialog.Modal>
  );
};

export default TeleportAccessRequestModal;
